/**
 * Voice-input constants shared by the composer, the recorder hook, and the
 * language gate.
 *
 * Mirrors ``votemate-mobile-app/src/constants/voice.ts``; the two clients ship
 * the same limits so a voter moving between them meets the same behavior.
 */

/**
 * Hard ceiling on one recording.
 *
 * Matched to the composer's 1000-character limit: ordinary speech runs about
 * 800 characters a minute, so a 60-second note lands just inside what the
 * message field accepts. The backend allows 75 seconds, and that headroom
 * absorbs container overhead rather than inviting longer recordings.
 */
export const VOICE_MAX_RECORDING_SECONDS = 60;

/** Seconds remaining at which the composer starts showing a countdown. */
export const VOICE_COUNTDOWN_THRESHOLD_SECONDS = 10;

/**
 * Interface locales with no Azure Speech transcription model.
 *
 * Haitian Creole is the whole list. Transcribing Creole through a French model
 * returns fluent, confident nonsense — worse for the speaker than no mic at
 * all — so the button is hidden rather than allowed to fail quietly. Mirrored
 * by ``UNSUPPORTED_LOCALES`` in ``votemate-backend/utils/voice_locales.py``,
 * which refuses these server-side too. Tracked as a known product gap.
 */
export const VOICE_UNSUPPORTED_LOCALES = ['ht'] as const;

/**
 * Containers to record in, best first.
 *
 * Every entry is one Azure Speech Fast Transcription decodes, so whichever the
 * browser picks uploads without a server-side transcode. Opus-in-WebM is the
 * smallest and works in Chrome, Firefox and Safari 18.4+; ``audio/mp4`` (AAC)
 * is the fallback that keeps older Safari working.
 */
export const VOICE_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
] as const;

/**
 * Whether VoteMate offers voice input in an interface locale.
 *
 * @param locale Active interface locale, e.g. ``es`` or ``zh-Hant``.
 */
export function isVoiceInputSupportedLocale(locale: string | undefined | null): boolean {
  if (!locale) return true;
  const base = locale.split('-')[0];
  return !VOICE_UNSUPPORTED_LOCALES.some((code) => code === locale || code === base);
}

/** The frame's Permissions Policy, which Chromium exposes and others do not. */
interface FramePermissionsPolicy {
  allowsFeature?: (feature: string) => boolean;
}

/**
 * Whether this frame is known to be barred from the microphone.
 *
 * A cross-origin iframe reaches the microphone only when the host page
 * delegates it via ``allow="microphone"``, and loaders already cached on
 * partner sites do not. ``getUserMedia`` stays defined in such a frame and
 * rejects only when called, so the policy is the one signal available up
 * front. Chromium reports it; Safari and Firefox do not, so there the mic
 * shows and a tap explains that access is off.
 */
function isMicrophoneBlockedByPolicy(): boolean {
  if (typeof document === 'undefined') return false;
  const doc = document as Document & {
    permissionsPolicy?: FramePermissionsPolicy;
    featurePolicy?: FramePermissionsPolicy;
  };
  const policy = doc.permissionsPolicy ?? doc.featurePolicy;
  return typeof policy?.allowsFeature === 'function' && !policy.allowsFeature('microphone');
}

/**
 * Whether this browser can record here.
 *
 * Doubles as the partner-embed gate where the browser reports the frame's
 * Permissions Policy (see ``isMicrophoneBlockedByPolicy``), with no
 * embed-specific branch to keep in sync.
 */
export function isVoiceRecordingSupported(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (!navigator.mediaDevices?.getUserMedia) return false;
  if (isMicrophoneBlockedByPolicy()) return false;
  if (typeof window.MediaRecorder === 'undefined') return false;
  return VOICE_MIME_CANDIDATES.some((type) => MediaRecorder.isTypeSupported(type));
}

/**
 * Pick the best container this browser can record.
 *
 * @returns The chosen MIME type, or ``null`` when none is supported.
 */
export function pickRecordingMimeType(): string | null {
  return VOICE_MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}
